/**
 * P3 — Client-report review API client (agency employee surface).
 *
 * Thin hand-written fetch wrapper matching roles-api.ts / identity-api.ts:
 *   - Bearer = the agency `emp_` key from Redux (`auth.agency.accessToken`).
 *   - `credentials: 'include'` so the httpOnly agency-session cookie also
 *     authenticates on a cold reload (same as roles-api).
 *   - `{ data }` envelope unwrapped; enriched `Error & { status, code }`.
 *   - Base URL `CENTRAL_API_BASE_URL` (= `${env}/api/v1/`).
 *
 * Backend contract (central-api Modules/ClientReporting, do NOT change it):
 *   GET  client-reports?status=&shop_slug=&period=YYYY|YYYY-MM&limit=
 *          [portal.reports.review] → { data: ClientReportSummary[] }
 *   GET  client-reports/{id}        → { data: ClientReportDetail }
 *   GET  client-reports/{id}/html?version=   → text/html (sanitized, assets resolved)
 *   POST client-reports/{id}/versions {html, edit_summary?}
 *          → 201 { data: ClientReportVersion, report: ClientReport }
 *   POST client-reports/{id}/approve {note?}          [portal.reports.approve]
 *   POST client-reports/{id}/reject {note}            [portal.reports.approve]
 *   POST client-reports/{id}/request-changes {note}   [portal.reports.approve]
 *   GET  client-reports/{id}/audit  → { data: ClientReportAuditEntry[] }
 *   POST client-portal/invite {shop_slug,email,name?} (ClientPortal module)
 *          → 201 { data:{client_login_id,shop_slug,email}, magic_link_url, expires_at, email_sent }
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- wire types (mirror ClientReport::toApiArray etc.) ----------

export type ReportPeriodType = 'weekly' | 'monthly' | 'quarterly' | 'custom';

export type ReportStatus =
  | 'pending_review'
  | 'changes_requested'
  | 'approved'
  | 'published'
  | 'rejected'
  | 'archived';

export type ReportCreatedByType = 'ai' | 'employee';

export interface ClientReportSummary {
  id: string;
  shop_id: number | string | null;
  shop_slug: string | null;
  title: string;
  period_type: ReportPeriodType;
  period_start: string | null;
  period_end: string | null;
  period_label: string | null;
  status: ReportStatus | string;
  current_version_id: string | null;
  created_by_type: ReportCreatedByType | string;
  created_by: string | null;
  source_metadata: Record<string, unknown> | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  published_at: string | null;
  client_notified_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface SanitizerReport {
  [key: string]: unknown;
}

export interface ClientReportVersion {
  id: string;
  report_id: string;
  version_number: number;
  html_sha256: string | null;
  size_bytes: number | null;
  /** null = the AI draft (v1); otherwise the employee UUID who saved it. */
  edited_by: string | null;
  edit_summary: string | null;
  sanitizer_report: SanitizerReport | null;
  created_at: string | null;
}

export interface ClientReportAuditEntry {
  id: string;
  actor_type: 'employee' | 'client' | 'system' | 'ai' | string;
  actor_id: string | null;
  action: string;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
}

export interface ClientReportDetail extends ClientReportSummary {
  versions: ClientReportVersion[];
  audit_tail: ClientReportAuditEntry[];
}

export interface StoreVersionResponse {
  data: ClientReportVersion;
  report: ClientReportSummary;
}

export interface ClientReportsApiError extends Error {
  status?: number;
  code?: string;
}

export interface ListReportsParams {
  status?: string;
  shop_slug?: string;
  /** `YYYY` (year) or `YYYY-MM` (month), matched against period_start. */
  period?: string;
  limit?: number;
}

// ---------- portal-client invite (ClientPortal module) ----------

export interface InviteClientBody {
  shop_slug: string;
  email: string;
  name?: string;
}

export interface InviteClientResult {
  data: {
    client_login_id: string;
    shop_slug: string;
    email: string;
  };
  magic_link_url: string;
  /** ISO datetime string (P2 shape — was epoch in Phase 6). */
  expires_at: string;
  email_sent: boolean;
}

// ---------- internal helpers (mirrors roles-api) ----------

function getAuthHeader(): Record<string, string> {
  const token = store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[client-reports-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function throwEnriched(response: Response): Promise<never> {
  let detail = '';
  let code: string | undefined;
  try {
    const body = await parseJson<{ message?: string; error?: string }>(response);
    code = body?.error;
    detail = body?.message || body?.error || '';
  } catch {
    /* fall back to status text */
  }
  const err = new Error(
    detail || `Request failed (${response.status} ${response.statusText})`
  ) as ClientReportsApiError;
  err.status = response.status;
  err.code = code;
  throw err;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeader(),
    ...((init.headers as Record<string, string>) || {}),
  };
  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });
  if (!response.ok) await throwEnriched(response);
  return parseJson<T>(response);
}

function toQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) usp.append(k, String(v));
  });
  const q = usp.toString();
  return q ? `?${q}` : '';
}

// ---------- endpoints ----------

export async function listClientReports(
  params: ListReportsParams = {}
): Promise<ClientReportSummary[]> {
  const res = await request<{ data?: ClientReportSummary[] }>(
    `client-reports${toQuery({
      status: params.status,
      shop_slug: params.shop_slug,
      period: params.period,
      limit: params.limit,
    })}`,
    { method: 'GET' }
  );
  return res.data ?? [];
}

export async function getClientReport(id: string): Promise<ClientReportDetail> {
  const res = await request<{ data: ClientReportDetail }>(
    `client-reports/${encodeURIComponent(id)}`,
    { method: 'GET' }
  );
  return res.data;
}

/**
 * GET the sanitized report HTML (text/html, NOT JSON). Assets are already
 * resolved to signed URLs server-side. `version` picks a specific version
 * number; omitting it serves the current version.
 */
export async function getClientReportHtml(
  id: string,
  version?: number
): Promise<string> {
  const response = await fetch(
    `${CENTRAL_API_BASE_URL}client-reports/${encodeURIComponent(id)}/html${toQuery(
      { version }
    )}`,
    { method: 'GET', headers: getAuthHeader(), credentials: 'include' }
  );
  if (!response.ok) await throwEnriched(response);
  return response.text();
}

export async function createReportVersion(
  id: string,
  html: string,
  editSummary?: string
): Promise<StoreVersionResponse> {
  return request<StoreVersionResponse>(
    `client-reports/${encodeURIComponent(id)}/versions`,
    {
      method: 'POST',
      body: JSON.stringify({ html, edit_summary: editSummary }),
    }
  );
}

export async function approveReport(
  id: string,
  note?: string
): Promise<ClientReportSummary> {
  const res = await request<{ data: ClientReportSummary }>(
    `client-reports/${encodeURIComponent(id)}/approve`,
    { method: 'POST', body: JSON.stringify({ note }) }
  );
  return res.data;
}

export async function rejectReport(
  id: string,
  note: string
): Promise<ClientReportSummary> {
  const res = await request<{ data: ClientReportSummary }>(
    `client-reports/${encodeURIComponent(id)}/reject`,
    { method: 'POST', body: JSON.stringify({ note }) }
  );
  return res.data;
}

export async function requestReportChanges(
  id: string,
  note: string
): Promise<ClientReportSummary> {
  const res = await request<{ data: ClientReportSummary }>(
    `client-reports/${encodeURIComponent(id)}/request-changes`,
    { method: 'POST', body: JSON.stringify({ note }) }
  );
  return res.data;
}

export async function getReportAudit(
  id: string
): Promise<ClientReportAuditEntry[]> {
  const res = await request<{ data?: ClientReportAuditEntry[] }>(
    `client-reports/${encodeURIComponent(id)}/audit`,
    { method: 'GET' }
  );
  return res.data ?? [];
}

/**
 * POST client-portal/invite — mint a magic link for a new portal client of a
 * brand. Returns the magic link (email send is best-effort server-side; the
 * FE always shows the link with a copy button as a fallback).
 *
 * TODO(backend, P1 gap): there is no list / resend / revoke endpoint for
 * portal clients yet (only invite exists). The clients page surfaces the
 * invite + returned link and marks the roster as a follow-up.
 */
export async function invitePortalClient(
  body: InviteClientBody
): Promise<InviteClientResult> {
  return request<InviteClientResult>('client-portal/invite', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------- portal-client roster (list / resend / revoke) ----------

export type PortalClientStatus = 'active' | 'invited' | 'revoked';

export interface PortalClient {
  /** client_logins UUID — the {client} path param for resend/revoke. */
  id: string;
  email: string;
  name: string | null;
  status: PortalClientStatus | string;
  /** ISO-8601, nullable. */
  last_login_at: string | null;
  /** ISO-8601, nullable. Equals created_at (the invite creates the row). */
  invited_at: string | null;
  created_at: string | null;
}

export interface ResendPortalClientResult {
  sent: boolean;
  magic_link_url: string;
  /** ISO-8601. */
  expires_at: string;
  email_sent: boolean;
}

export interface RevokePortalClientResult {
  revoked: boolean;
}

/**
 * GET client-portal/clients?shop_slug= — the roster of a brand's portal
 * clients (owner/team_lead/manager, [portal.clients.manage]). Rows are scoped
 * to the caller's org; revoked rows are retained with status='revoked'.
 */
export async function listPortalClients(
  shopSlug: string
): Promise<PortalClient[]> {
  const res = await request<{ clients?: PortalClient[] }>(
    `client-portal/clients${toQuery({ shop_slug: shopSlug })}`,
    { method: 'GET' }
  );
  return res.clients ?? [];
}

/**
 * POST client-portal/clients/{id}/resend — re-mint a FRESH magic link for an
 * existing active client and re-send the invite email (best-effort). Returns
 * the link so the caller can copy/share it out-of-band.
 *
 * 404 `client_not_found` (unknown/cross-org); 409 `client_revoked` (inactive).
 */
export async function resendPortalClient(
  clientId: string
): Promise<ResendPortalClientResult> {
  return request<ResendPortalClientResult>(
    `client-portal/clients/${encodeURIComponent(clientId)}/resend`,
    { method: 'POST' }
  );
}

/**
 * POST client-portal/clients/{id}/revoke — revoke a portal client's access
 * (deactivates the row, clears live session + invite token + pending OTP).
 * Idempotent. 404 `client_not_found` (unknown/cross-org).
 */
export async function revokePortalClient(
  clientId: string
): Promise<RevokePortalClientResult> {
  return request<RevokePortalClientResult>(
    `client-portal/clients/${encodeURIComponent(clientId)}/revoke`,
    { method: 'POST' }
  );
}

export interface RefreshPortalLinkResult {
  data: {
    shop_slug: string;
    client_email: string;
    magic_link_url: string;
    expires_at: string;
  };
  message: string;
}

/**
 * POST client-portal/refresh-link — the "Refresh link" action. Mints a FRESH
 * working magic link for the brand's active portal client and returns its URL
 * (the caller copies it to the clipboard). The portal enforces expiry
 * client-side, so a new magic link — not a DB reset — is what re-authenticates.
 * 404 `no_active_client` when the brand has no active portal client yet.
 */
export async function refreshPortalLink(
  shopSlug: string
): Promise<RefreshPortalLinkResult> {
  return request<RefreshPortalLinkResult>('client-portal/refresh-link', {
    method: 'POST',
    body: JSON.stringify({ shop_slug: shopSlug }),
  });
}
